import isEqual from 'lodash/isEqual';
import {jsx} from 'panel';
import {VNode} from 'snabbdom';

import {ReportQueryBuilder, ReportQueryBuilderParams} from 'common/report/report-manager/query-builder';
import {isRetentionType, RetentionBookmarkParams} from 'common/report/retention/util/types';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {ChartType, ReportName} from 'common/report/constants';
import {QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {QueryContext} from 'common/query-context';
import {
  CountBy,
  QueryEventEntry,
  QuerySuperEntry,
  RetentionCountType,
} from 'common/query-builder/query-entry-section/types';
import {
  getAttributionSelection,
  getBookmarkUpdatesFromEntries,
  getBookmarkUpdatesFromFilters,
  getBookmarkUpdatesFromGroupBy,
  getBreakdownAttribution,
  getQBEntries,
  getQBFilters,
  getQBGroupBy,
  nullIfEmpty,
  RETENTION_DEFAULT_EVENT,
  RetentionQuery,
} from 'common/report/retention/models/retention-query';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  RetentionAlignmentUpdate,
  RetentionBucketSizeUpdate,
  RetentionCriteriaEl,
  RetentionUnitUpdate,
} from 'common/report/retention/query-builder/retention-criteria';
import {
  RetentionQuerySettingsEl,
  RetentionQuerySettingsUpdate,
} from 'common/report/retention/query-builder/retention-query-settings';
import {emitTrackEvent} from 'common/util/dom';
import {RetentionTrackingEvents, RetentionTrackingProperties} from 'common/report/retention/util/constants';
import {FullLengthDateRange} from 'common/widgets/date-range-picker/models/full-length-date-range';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {BehaviorType} from 'common/query-builder/query-block/constants';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize} from 'common/components/button/constants';
import {Placement} from 'common/components/tooltip/types';
import {dataGroupStore} from 'common/report/stores/data-groups';

import {SuccessResponse} from '../models/types';
import {generateRetentionBucketSectionItems} from '../util/parsing';

type RetentionQueryBuilderState = EmptyObj;

type RetentionQueryBuilderJsxProps = {
  params: ReportQueryBuilderParams<RetentionBookmarkParams>;
};

const RETENTION_TYPE_OPTIONS = [
  {
    value: `birth`,
    label: `Retention`,
    tooltipLabel: `**Retention Analysis**
    How many users return
    every day, week, or month?
    [Learn More](https://docs.mixpanel.com/docs/analysis/reports/retention#overview)`,
  },
  {
    value: `addiction`,
    label: `Frequency`,
    tooltipLabel: `**Frequency Analysis**
  How frequently do your users
  return to your product?
  [Learn More](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)`,
  },
];

const REPORT_ROW_LIMITS = {
  BIRTH: 2,
  OTHER: 1,
};

enum ReportHeaders {
  Birth = `Retention Events`,
  Other = `Frequency Events`,
}

const MAX_NUMBER_OF_GROUPBYS = 3;

export function getRetentionReportQueryContext(
  currentQuery: RetentionQuery,
  {originDashboard, customEvents}: {originDashboard: DashboardOrigin; customEvents: CustomEventDefinition[]},
): null | QueryContext {
  const currentParams = currentQuery.generateBookmark();
  const defaultParams = new RetentionQuery({});

  if (!isEqual(currentParams, defaultParams.generateBookmark())) {
    const dateRange = DateRange.fromBookmarkFormat(currentQuery.dateRange);
    const dataGroupId = currentQuery.dataGroupId;
    const entries = getQBEntries(currentQuery, customEvents);

    const events: QueryEventEntry[] = entries
      .filter((entry) => entry.event.value && entry.event.value !== `$mp_anything_event`)
      .map((entry) => {
        return {
          type: entry.type,
          event: entry.event,
          filters: entry.filters,
          filtersOperator: entry.filtersOperator,
        };
      });

    const comparedDateRange = currentQuery.timeComparison;
    const segments = getQBGroupBy(currentQuery.groupBy);
    const filters = getQBFilters(currentQuery.filterByEvent, currentQuery.filterByCohort);

    return {
      bookmarkType: BookmarkType.Retention,
      dataGroupId,
      dateRange,
      querySamplingEnabled: currentQuery.querySampling,
      ...(events.length && {events}),
      ...(comparedDateRange && {comparedDateRange}),
      ...(originDashboard && {originDashboard}),
      ...(segments && {segments}),
      ...(filters && {filters}),
    };
  }

  return null;
}

export function getRetentionQueryFromQueryContext(queryContext: QueryContext): RetentionQuery {
  const events =
    queryContext.events &&
    queryContext.events.filter((entry) => ![`$session_start`, `$session_end`].includes(entry.event.value)).slice(0, 2);

  let attributionSelection;
  if (events && events.length > 0) {
    attributionSelection = events[0].event.value;
  }

  const query = new RetentionQuery();
  return query.createFromUpdate({
    ...(queryContext.querySamplingEnabled && {querySampling: true}),
    ...(queryContext.dataGroupId && {dataGroupId: queryContext.dataGroupId}),
    ...(queryContext.dateRange && {dateRange: new FullLengthDateRange(queryContext.dateRange).getBookmarkFormat()}),
    ...(events && events.length > 0 && getBookmarkUpdatesFromEntries(events, query)),
    ...(queryContext.segments && getBookmarkUpdatesFromGroupBy(queryContext.segments, query, attributionSelection)),
    ...(queryContext.filters && getBookmarkUpdatesFromFilters(queryContext.filters)),
    // Disable date range comparison in retention - retention curve does not support time comparison
    // Retention trends does support it, so if we change the default report to retention trends we can add back in
    // time comparison from a previous context
  });
}

export function getRetentionReportSpecificQBParams(
  query: RetentionQuery,
  {
    customEvents,
    canUseDataGroups,
  }: {
    customEvents: CustomEventDefinition[];
    canUseDataGroups: boolean;
  },
) {
  const breakdownAttribution = getBreakdownAttribution(query, customEvents);
  const attributionSelection = getAttributionSelection(query, customEvents);
  const {dataGroupId, dateRange, filterByCohort, filterByEvent, groupBy, retentionType} = query;
  const entries = getQBEntries(query, customEvents);

  const filters = getQBFilters(filterByEvent, filterByCohort);

  const queryBuilderGroupBy = getQBGroupBy(groupBy);

  return {
    breakdownAttributionSelection: attributionSelection,
    breakdownAttribution,
    canCustomizeNumericBuckets: true,
    dataGroupId,
    dateRange: DateRange.fromBookmarkFormat(dateRange),
    disableCloneEntry: true,
    disableAddEntry: true,
    disableFirstTimeEventFilters: retentionType !== `birth`,
    enableEnhancedListFilters: true,
    entries,
    minEntries: entries.length,
    emptyPlaceholder: RETENTION_DEFAULT_EVENT as QuerySuperEntry,
    entriesDraggable: true,
    entryRowLabelType: `alpha`,
    filters,
    groupBy: queryBuilderGroupBy,
    hideDataGroupsSelector: false,
    hideDatePicker: true,
    headerTitle: retentionType === `birth` ? ReportHeaders.Birth : ReportHeaders.Other,
    includeCustomProperties: `all`,
    groupByLimitation: MAX_NUMBER_OF_GROUPBYS,
    rowLimit: retentionType === `birth` ? REPORT_ROW_LIMITS.BIRTH : REPORT_ROW_LIMITS.OTHER,
    queryTypeOptions: RETENTION_TYPE_OPTIONS,
    selectedQueryType: retentionType,
    showAnyEvent: true,
    showEntryRename: false,
    showExclusionSteps: false,
    supportB2bCohorts: canUseDataGroups,
    showSpecialDateProperty: true,
  };
}

export function getRetentionQueryUpdatesFromQueryBuilderUpdate(
  query: RetentionQuery,
  qbUpdate: QueryBuilderUpdateDetail,
): Partial<RetentionQuery> {
  const entries = qbUpdate.entries as QueryEventEntry[];
  const {filters: updatedFilters, groupBy, attributionSelection, dataGroupId, queryType} = qbUpdate;

  let queryUpdates: Partial<RetentionQuery>;
  if (isRetentionType(queryType)) {
    // Report toggle got updated
    queryUpdates = query.createFromRetentionTypeUpdate(queryType);
  } else {
    queryUpdates = {
      dataGroupId: nullIfEmpty(dataGroupId),
      segmentationEvent: groupBy.length === 0 ? null : nullIfEmpty(attributionSelection?.toString()),
    };
    Object.assign(queryUpdates, getBookmarkUpdatesFromFilters(updatedFilters));
    Object.assign(queryUpdates, getBookmarkUpdatesFromGroupBy(groupBy, query, attributionSelection));
    Object.assign(queryUpdates, getBookmarkUpdatesFromEntries(entries, query));
  }

  return queryUpdates;
}

class RetentionQueryBuilder extends ReportQueryBuilder<
  RetentionBookmarkParams,
  SuccessResponse,
  RetentionQueryBuilderState
> {
  displayContext = SearchDisplayContext.Retention;
  reportType = ReportName.Retention;

  get defaultState(): RetentionQueryBuilderState {
    return {};
  }

  bookmarkUpdateFromQbUpdate(qbUpdate: QueryBuilderUpdateDetail): RetentionBookmarkParams {
    const query = RetentionQuery.createFromBookmark(this.params.bookmarkParams);
    const queryUpdates = getRetentionQueryUpdatesFromQueryBuilderUpdate(query, qbUpdate);
    const newQuery = query.createFromUpdate(queryUpdates);
    return newQuery.generateBookmark();
  }

  protected qbChildrenTemplate(): VNode {
    const query = RetentionQuery.createFromBookmark(this.params.bookmarkParams);
    const shouldShowRetentionCriteria = !query.isAddiction();
    return (
      <>
        {shouldShowRetentionCriteria ? (
          <RetentionCriteriaEl
            attrs={{
              unbounded: query.unbounded,
              'retention-unit': query.retentionUnit,
              'bucket-sizes': query.bucketSizes,
              'retention-alignment-type': query.retentionAlignmentType,
              'data-group-id': query.dataGroupId,
            }}
            on={{
              changeSetting: this.handleSettingsChanged,
              changeUnit: this.handleRetentionUnitChanged,
              applyBucketSizes: this.handleBucketSizesApplied,
              'toggle-retention-alignment': this.handleToggleRetentionAlignment,
            }}
          />
        ) : (
          <RetentionQuerySettingsEl
            attrs={{
              'retention-type': query.retentionType,
              'counting-method': query.getCountingMethod(),
            }}
            on={{change: this.handleSettingsChanged}}
          />
        )}
      </>
    );
  }

  protected qbCountMenuTemplate(): VNode {
    const query = RetentionQuery.createFromBookmark(this.params.bookmarkParams);

    const showAdvancedMenu =
      query.chartType === ChartType.RetentionTrend || query.chartType === ChartType.RetentionTrendMetric;
    return (
      <QueryBlockEl
        sel='.count-menu'
        attrs={{
          elref: `countBlock`,
          type: `count`,
          'behavior-type': BehaviorType.Retention,
          'data-group-id': query.dataGroupId || ``,
          'data-groups': Object.values(dataGroupStore.value),
          'project-id': this.params.projectId,
          'selector-type': `button`,
          selected: {
            countType: query.percentageView ? RetentionCountType.RetentionRate : RetentionCountType.Unique,
          },
          'advanced-menu-sections': showAdvancedMenu
            ? [
                {
                  label: `Retention groups`,
                  icon: `info-xs`,
                  tooltip: {
                    'max-width': `120px`,
                    placement: Placement.Right,
                    text: `View how specific Retention Groups trend over time. [Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-trends)`,
                  },
                  items: generateRetentionBucketSectionItems({
                    results: this.params.results,
                    query,
                    timezone: this.params.project?.timezone,
                  }),
                },
              ]
            : [],
          'highlight-selections': true,
          theme: SelectTheme.QueryBuilder,
          'button-size': ButtonSize.Small,
          'track-name': `retention`,
          'user-id': this.params.userId,
          'workspace-id': this.params.workspaceId,
        }}
        on={{
          select: this.handleCountByChanged,
        }}
      />
    );
  }

  protected reportSpecificQBParams() {
    const query = RetentionQuery.createFromBookmark(this.params.bookmarkParams);
    return getRetentionReportSpecificQBParams(query, {
      customEvents: this.params.customEvents,
      canUseDataGroups: this.canUseDataGroups(),
    });
  }

  // note: returns an updated RetentionQuery model object to make it convenient to use model-based logic afterwards
  private updateBookmarkParamsFromModel = (
    queryUpdates: Partial<RetentionQuery>,
    {
      queryReason,
      requery = true,
    }: {
      queryReason: QueryReason;
      requery?: boolean;
    },
  ): RetentionQuery => {
    const query = RetentionQuery.createFromBookmark(this.params.bookmarkParams);

    let adjustedQueryUpdates = queryUpdates;
    if (`retentionUnit` in adjustedQueryUpdates && query.shouldSyncDateAndRetentionUnits()) {
      adjustedQueryUpdates = {
        ...adjustedQueryUpdates,
        dateUnit: adjustedQueryUpdates.retentionUnit,
      };
    }

    const newQuery = query.createFromUpdate(adjustedQueryUpdates);
    this.dispatchUpdate({params: newQuery.generateBookmark(), requery, queryReason});
    return newQuery;
  };

  private handleSettingsChanged = (update: RetentionQuerySettingsUpdate) => {
    const query = this.updateBookmarkParamsFromModel(update.detail, {
      queryReason: QueryReason.QbOtherUpdate,
      requery: true,
    });

    const isAddiction = query.retentionType === `addiction`;
    let selection;
    if (isAddiction) {
      selection = update.detail.cumulative
        ? RetentionTrackingProperties.Cumulative
        : RetentionTrackingProperties.NonCumulative;
    } else {
      selection = update.detail.unbounded ? RetentionTrackingProperties.Unbounded : RetentionTrackingProperties.Bounded;
    }
    emitTrackEvent(this, {
      eventName: isAddiction ? RetentionTrackingEvents.ChangeCumulative : RetentionTrackingEvents.ChangeUnbounded,
      eventPrefix: `mp-query-builder`,
      props: {
        report: `retention`,
        counting_method: selection,
      },
    });
  };

  private handleRetentionUnitChanged = (update: RetentionUnitUpdate) => {
    const {retentionUnit} = update.detail;
    const bucketSizes = [];
    this.updateBookmarkParamsFromModel(
      {
        retentionUnit,
        bucketSizes,
      },
      {
        queryReason: QueryReason.QbOtherUpdate,
        requery: true,
      },
    );
  };

  private handleBucketSizesApplied = (update: RetentionBucketSizeUpdate) => {
    const {retentionUnit, bucketSizes} = update.detail;
    this.updateBookmarkParamsFromModel(
      {
        retentionUnit,
        bucketSizes,
      },
      {
        queryReason: QueryReason.QbOtherUpdate,
        requery: true,
      },
    );
  };

  private handleToggleRetentionAlignment = (update: RetentionAlignmentUpdate) => {
    const {retentionAlignmentType} = update.detail;
    this.updateBookmarkParamsFromModel(
      {
        retentionAlignmentType,
      },
      {
        queryReason: QueryReason.QbOtherUpdate,
        requery: true,
      },
    );
  };

  // for now, we only support changing RetentionRate <-> Unique
  // eventually we need to support property sum here as well.
  private handleCountByChanged = (ev: CustomEvent<{item: CountBy}>) => {
    this.updateBookmarkParamsFromModel(
      {
        percentageView: ev.detail.item.countType === RetentionCountType.RetentionRate,
        retentionBucket: ev.detail.item.advancedMenuValue as number,
      },
      {
        queryReason: QueryReason.QbOtherUpdate,
        requery: false,
      },
    );
  };
}

export const RetentionQueryBuilderEl = defineAndMakeJsxFactory<RetentionQueryBuilderJsxProps>(
  `retention-query-builder`,
  RetentionQueryBuilder,
);
export const RetentionQueryBuilderReact = panel2react<RetentionQueryBuilderJsxProps>(
  `retention-query-builder`,
  RetentionQueryBuilder,
);
